import { useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { _object } from 'utils/interfaces'
import { Button, InputField } from '../form-inputs'
import { sliderService } from 'services/slider.service'
import { toast } from 'react-toastify'
import store from 'store'

const SliderAddModal = () => {
  const [loading, setLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null
    if (file) {
      setSelectedFile(file)
    }
  }
  const validationSchema = Yup.object({
    title: Yup.string().label('Title').required(),
    target_link: Yup.string().label('Target Link').required()
  })

  const formik: _object = useFormik({
    initialValues: {
      title: '',
      target_link: '',
      status: 'active',
      target_label: '',
      subtitle: ''

    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      setLoading(true)
      sliderService.addSlider(values).then((results: _object) => {
        setLoading(false)
        if (results?.error === false) {
          toast.success(results.message || 'Slide added successfully.', { autoClose: 3000 })

          formik.resetForm()
          setSelectedFile(null)

          const closeModal = () => {
            const modalEl = document.getElementById('slider-add-modal')
            if (modalEl) modalEl.classList.add('hidden')
          }

          if (!selectedFile) {
            window.dispatchEvent(new Event('sliderAdded'))
            closeModal()
            return
          }

          setIsUploading(true)

          const uploadImage = async () => {
            const data = new FormData()

            if (results.data.id) {
              data.append('entity_id', results.data.id)
            }
            data.append('entity_type', 'slider')
            data.append('key', 'slider_image')
            data.append('files', selectedFile as File)
            const accessToken = store.get(process.env.REACT_APP_ACCESS_TOKEN_KEY || '')

            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/media`, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                Authorization: accessToken ? accessToken : ''
              },
              body: data
            })

            const result = await response.json()
            if (result.error === false) {
              toast.success(result.message || 'Image uploaded successfully.', {
                autoClose: 3000
              })
              // notify list to refresh
              window.dispatchEvent(new Event('sliderAdded'))
              closeModal()
            } else if (result.error === true) {
              toast.error(result.message || 'Image not uploaded.', {
                autoClose: 3000
              })
            }
            setIsUploading(false)
          }

          uploadImage()
        } else if (results?.error === true) {
          toast.error(results.message || 'Failed to add slide.', { autoClose: 3000 })
        }
      })
    }
  })

  return (
    <div
      id="slider-add-modal"
      tabIndex={-1}
      aria-hidden="true"
      className="fixed top-0 right-0 left-0 z-50 flex hidden h-[calc(100%-1rem)] max-h-full w-full items-center justify-center overflow-x-hidden overflow-y-auto md:inset-0"
    >
      {/* Background Overlay */}
      <div className="relative max-h-full w-full max-w-2xl p-4">
        {/* Modal Content */}
        <div className="relative z-10 max-h-full w-full max-w-2xl p-4">
          <div className="relative rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between rounded-t border-b border-gray-200 p-3 px-5">
              <h3 className="text-lg font-semibold">Add New Slide</h3>
              <button
                type="button"
                className="ms-auto inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm font-medium text-gray-400"
                data-modal-hide="slider-add-modal"
                onClick={() => {
                  formik.resetForm()
                  setSelectedFile(null)
                  const modalEl = document.getElementById('slider-add-modal')
                  if (modalEl) modalEl.classList.add('hidden')
                }}
              >
                <svg
                  className="h-3 w-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>
            <form className="p-5" onSubmit={formik.handleSubmit}>
              <InputField
                label="Title"
                name="title"
                type="text"
                placeholder="Title"
                onChange={formik.handleChange}
                value={formik.values.title}
                onBlur={formik.handleBlur}
                required={true}
                error={formik.touched.title && formik.errors.title}
              />

              <div className="mb-5">
                <label
                  htmlFor="target_link"
                  className="mb-2 flex text-sm leading-none font-medium text-gray-700"
                >
                  Target Link <span className='text-red-500'>*</span>
                </label>

                <InputField
                  id="target_link"
                  name="target_link"
                  onChange={formik.handleChange}
                  value={formik.values.target_link}
                  className="w-full rounded-md border border-transparent bg-gray-100 px-3 py-2 text-black placeholder-gray-500 focus:border-gray-400 focus:outline-none"
                  placeholder="Target link"
                />
                <div className="mb-5">
                  <label
                    htmlFor="target_label"
                    className="mb-2 flex text-sm leading-none font-medium text-gray-700"
                  >
                    Target Label
                  </label>

                  <InputField
                    id="target_label"
                    name="target_label"
                    onChange={formik.handleChange}
                    value={formik.values.target_label}
                    className="w-full rounded-md border border-transparent bg-gray-100 px-3 py-2 text-black placeholder-gray-500 focus:border-gray-400 focus:outline-none"
                    placeholder="Button label or target text"
                  />
                </div>
                <div className="mb-5">
                  <label className="mb-2 flex text-sm leading-none font-medium text-gray-700">
                    Status
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="status"
                        value="active"
                        checked={formik.values.status === 'active'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                      />
                      Active
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="status"
                        value="inactive"
                        checked={formik.values.status === 'inactive'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                      />
                      Inactive
                    </label>
                  </div>
                </div>
                <div className="mb-5">
                  <label
                    htmlFor="subtitle"
                    className="mb-2 flex text-sm leading-none font-medium text-gray-700"
                  >
                    Subtitle
                  </label>

                  <textarea
                    id="subtitle"
                    name="subtitle"
                    onChange={formik.handleChange}
                    value={formik.values.subtitle}
                    rows={3}
                    className="w-full rounded-md border border-transparent bg-gray-100 px-3 py-2 text-black placeholder-gray-500 focus:border-gray-400 focus:outline-none"
                    placeholder="Subtitle or short description"
                  ></textarea>
                </div>
              </div>
              <label className="mb-2 flex text-sm leading-none font-medium text-gray-700">
                Slide Image <span className="text-red-500">*</span>
              </label>
              <h3 className="mb-2 text-[10px] font-medium text-[#969494]">1904W × 1072H | 16:9</h3>

              <div className="flex items-center gap-4">
                <label htmlFor="imageUpload">
                  <div className="flex h-32 w-32 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-purple-900">
                    <input
                      type="file"
                      id="imageUpload"
                      accept=".png, .jpg, .jpeg"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    {isUploading ? (
                      <svg
                        className="h-8 w-8 animate-spin text-purple-900"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                    ) : (
                      <span className="text-4xl font-bold text-purple-900">+</span>
                    )}
                  </div>
                </label>
                {selectedFile && (
                  <img
                    src={URL.createObjectURL(selectedFile)}
                    alt="Preview"
                    className="h-32 w-40 rounded-md border object-cover"
                  />
                )}
              </div>
              <div className="mt-3">
                <Button
                  label="Add"
                  type="submit"
                  className="rounded-lg bg-purple-900 py-2 text-center text-sm text-white transition hover:bg-purple-950"
                  loading={loading}
                  disabled={!formik.isValid || !formik.dirty}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SliderAddModal
